import { useEffect, useMemo } from "react";

import dayjs from "@eisox/dayjs";
import { useQuery, type QueryFunction, type QueryKey } from "@tanstack/react-query";

import {
  buildStateHistoryDayWindow,
  findClosestDate,
  getStateHistoryDays,
  STATE_HISTORY_DATES_STALE_TIME_MS,
} from "~/helpers/history.helper";

interface UseStateHistoryDatesOptions<TKey extends QueryKey> {
  enabled: boolean;
  date?: string;
  now: Date;
  queryOptions: (startDate: string, endDate: string) => { queryKey: TKey; queryFn: QueryFunction<string[], TKey> };
  onDateChange: (date: string) => void;
}

export const useStateHistoryDates = <TKey extends QueryKey>({
  enabled,
  date,
  now,
  queryOptions,
  onDateChange,
}: UseStateHistoryDatesOptions<TKey>) => {
  const days = useMemo(() => getStateHistoryDays(now), [now]);
  const day = dayjs(date ?? now).format("YYYY-MM-DD");
  const { startDate, endDate } = useMemo(() => buildStateHistoryDayWindow(day), [day]);

  const query = useQuery({
    ...queryOptions(startDate, endDate),
    enabled,
    staleTime: STATE_HISTORY_DATES_STALE_TIME_MS,
    meta: { skipGlobalLoader: true },
  });

  useEffect(() => {
    if (!enabled || !query.data) return;
    const sample = date ? findClosestDate(query.data, date) : query.data.at(0);
    if (sample && sample !== date) onDateChange(sample);
  }, [enabled, query.data, date, onDateChange]);

  return { days, dates: query.data ?? [], isLoading: query.isLoading };
};
